import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { newId, PLAY_BEARING_NOTE_MAX } from '@gk/contracts'
import { useClub } from '../../club/ClubProvider.tsx'
import { compass8 } from '../../data/guide.ts'
import { frameFor } from '../../data/lookGeometry.ts'
import { PLAY_LINE_TYPES, sourceCodes, usePlayLines, wholeDegrees, type UnknownReason } from '../../data/playLine.ts'
import { formatLocal } from '../../lib/time.ts'
import { PendingBanner } from '../../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../../ui/hooks.ts'
import { OutlineSchema } from '../../ui/looks/OutlineSchema.tsx'

/** M25-L (docs/M25.md C and E, rule 6; the owner's answer 5 of 2026-10-28): « SENS DE JEU » of one area — its real outline turned so that the play arrives
 *  at the bottom, the bearing in force and where it came from: computed from the course tree (« calculé : F1 → G1 », with its caveat for the doglegs) or
 *  set by the club (the computed one still shown beside it, for checking); unknown said in words with why, on the generic drawing (never guessed).
 *  For `area.edit` (the owner and the Second; never on a workstation): « Ajuster » turns the line by 15° (↺ / ↻), with a note, and « Enregistrer » sends
 *  area.set_play_bearing@1 (an office intent, sendAndWait; the row comes back with the next pull); « Revenir au calcul » sends a null bearing. Self-contained
 *  (it reads the mirror itself): a page places `<PlayLinePanel areaId={…} />` — the map's area popup (L) and the course page (D). Nothing is shown for an
 *  area type the rule gives no line (a bunker, a hole…). The line of play turns this drawing and nothing else (rule 5). */
export function PlayLinePanel({ areaId }: { areaId: string }) {
  const { t, i18n } = useTranslation()
  const { site, kiosk } = useClub()
  const edit = useWriteAccess('area.edit')
  const canEdit = edit.allowed && !kiosk
  const data = usePlayLines()
  const action = useWorkshopAction()
  const [draft, setDraft] = useState<number | null>(null)
  const [note, setNote] = useState('')
  const [id, setId] = useState(() => newId())
  const self = data?.areas.find((a) => a.id === areaId)
  if (!data || !self || !(PLAY_LINE_TYPES as readonly string[]).includes(self.area_type_code)) return null
  const line = data.lines.get(areaId)
  if (!line) return null
  const deg = (b: number) => t('course.playLine.degrees', { deg: wholeDegrees(b), dir: t(`guide.dir.${compass8(wholeDegrees(b))}`) })
  const codes = sourceCodes(line.computed, data.areas)
  const fromTo = codes ? t('course.playLine.computedFrom', { from: codes.from.join(', '), to: codes.to.join(', ') }) : null
  const manual = line.source === 'manual' ? line.inForce : null
  const adjusting = draft !== null
  const shown = adjusting ? draft : line.bearing
  const trimmed = note.trim()
  const unchanged = adjusting && manual !== null && manual.bearing_deg === draft && (manual.note ?? '') === trimmed
  const send = async (bearing: number | null) => {
    const ok = await action.run('area.set_play_bearing@1', { id, area_id: areaId, bearing_deg: bearing, ...(bearing !== null && trimmed ? { note: trimmed } : {}) }, 'course.playLine.saved')
    if (ok) { setId(newId()); setDraft(null); setNote('') }
  }
  const reason = (r: UnknownReason) => t(`course.playLine.reason.${r}`)
  return (
    <section className="flex flex-col gap-2 border-t border-slate-200 pt-2 dark:border-night-line" data-testid="play-line" data-area={areaId} data-source={line.source}
      data-bearing={line.bearing === null ? '' : String(wholeDegrees(line.bearing))}>
      <h3 className="text-base font-bold">{t('course.playLine.title')}</h3>
      <div className="flex items-start gap-3">
        <span className="text-brand dark:text-fairway">
          <OutlineSchema geom={data.shapes.get(areaId)} bearing={shown} code={null} frame={frameFor([self.area_type_code])} playArrow showUnknown={false} className="size-20 shrink-0" testId="play-line-outline" />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          {line.bearing !== null
            ? <p className="font-semibold" data-testid="play-line-value">{deg(line.bearing)}</p>
            : <p className="font-semibold" data-testid="play-line-unknown">{t(self.area_type_code === 'green' ? 'looks.outline.unknown' : 'course.playLine.unknownArea')}</p>}
          {manual && <p className="text-sm" data-testid="play-line-source">{t('course.playLine.byClub', { date: formatLocal(manual.set_at, site.timezone, 'dd/MM/yyyy') })}</p>}
          {manual?.note && <p className="text-sm italic" data-testid="play-line-note">{manual.note}</p>}
          {line.computed.known
            ? <p className="meta" data-testid="play-line-computed">{manual ? t('course.playLine.computedBeside', { value: deg(line.computed.bearing), from: fromTo }) : fromTo}</p>
            : <p className="meta" data-testid="play-line-reason">{manual ? t('course.playLine.computedNone', { reason: reason(line.computed.reason) }) : reason(line.computed.reason)}</p>}
        </div>
      </div>
      <p className="meta" data-testid="play-line-approx">{t('course.playLine.approx')}</p>
      {canEdit && !adjusting && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn !px-4" onClick={() => { action.reset(); setDraft(wholeDegrees(line.bearing ?? 0)); setNote(manual?.note ?? '') }} data-testid="play-line-adjust">{t('course.playLine.adjust')}</button>
          {manual && <button type="button" className="btn !px-4" disabled={action.busy} onClick={() => void send(null)} data-testid="play-line-reset">{t('course.playLine.reset')}</button>}
        </div>
      )}
      {canEdit && adjusting && (
        <div className="flex flex-col gap-2 rounded-xl border-2 border-slate-200 p-3 dark:border-night-line" data-testid="play-line-editor">
          <p className="font-semibold" data-testid="play-line-draft" aria-live="polite">{deg(draft)}</p>
          <p className="meta">{t('course.playLine.adjustHelp')}</p>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn !px-4" onClick={() => setDraft(wholeDegrees(draft - 15))} aria-label={t('course.playLine.leftLabel')} data-testid="play-line-left">{t('course.playLine.left')}</button>
            <button type="button" className="btn !px-4" onClick={() => setDraft(wholeDegrees(draft + 15))} aria-label={t('course.playLine.rightLabel')} data-testid="play-line-right">{t('course.playLine.right')}</button>
          </div>
          <label className="flex flex-col gap-1 text-sm font-semibold" htmlFor={`play-line-note-${areaId}`}>
            {t('course.playLine.note')}
            <input id={`play-line-note-${areaId}`} className="input" value={note} maxLength={PLAY_BEARING_NOTE_MAX} onChange={(e) => setNote(e.target.value)} data-testid="play-line-note-input" />
          </label>
          <div className="flex flex-wrap gap-2">
            <button type="button" className="btn btn-primary !px-4" disabled={action.busy || unchanged} onClick={() => void send(draft)} data-testid="play-line-save">{t('course.playLine.save')}</button>
            <button type="button" className="btn !px-4" onClick={() => { setDraft(null); action.reset() }} data-testid="play-line-cancel">{t('common.cancel')}</button>
          </div>
          {unchanged && <p className="meta" data-testid="play-line-unchanged">{t('course.playLine.unchanged')}</p>}
        </div>
      )}
      {action.pending && <PendingBanner />}
      {action.error && <p className="text-sm font-semibold text-red-800 dark:text-red-300" role="alert" data-testid="play-line-error">{action.error}</p>}
      {action.ok && <p className="text-sm font-semibold text-brand dark:text-fairway" role="status" data-testid="play-line-ok">{action.ok}</p>}
    </section>
  )
}
